import { useState } from 'react';
import { useUsersPaginated, useStores, useCreateUser, useUpdateUser, useDeleteUser, usePagination } from '@/hooks';
import type { UserWithoutPassword } from '@geely/shared';
import type { UserFormData } from '@geely/shared/schemas';
import { toast } from 'sonner';

export function useUsersData() {
  const { page, limit, setPage } = usePagination();
  const { data: paginatedResult, isLoading: usersLoading } = useUsersPaginated({ page, limit });
  const users = paginatedResult?.data ?? [];
  const paginationMeta = paginatedResult?.meta;
  const { data: stores = [] } = useStores();
  const createUser = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithoutPassword | null>(null);

  const handleOpenDialog = (user?: UserWithoutPassword) => {
    setEditingUser(user || null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingUser(null);
  };

  const handleSubmit = async (data: UserFormData) => {
    const { _isEditing, confirmPassword, ...formFields } = data;

    try {
      if (editingUser) {
        const payload: Record<string, any> = {
          name: formFields.name,
          email: formFields.email,
          role: formFields.role,
          storeIds: formFields.role === 'admin' ? [] : formFields.storeIds,
        };

        if (formFields.password && formFields.password.length > 0) {
          payload.password = formFields.password;
        }

        await updateUserMutation.mutateAsync({ id: editingUser.id, data: payload });
        toast.success('Usuário atualizado com sucesso!');
      } else {
        await createUser.mutateAsync({
          name: formFields.name,
          email: formFields.email,
          password: formFields.password!,
          role: formFields.role,
          storeIds: formFields.role === 'admin' ? [] : formFields.storeIds,
        });
        toast.success('Usuário criado com sucesso!');
      }
      handleCloseDialog();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await deleteUserMutation.mutateAsync(id);
        toast.success('Usuário excluído com sucesso!');
      } catch (err: any) {
        toast.error(err.response?.data?.error || 'Erro ao excluir usuário');
      }
    }
  };

  const getStoreNames = (storeIds: string[]) => {
    if (!storeIds || storeIds.length === 0) return '-';
    return storeIds
      .map((id) => stores.find((s) => s.id === id)?.name || 'Loja não encontrada')
      .join(', ');
  };

  return {
    users,
    stores,
    isLoading: usersLoading,
    paginationMeta,
    setPage,
    isDialogOpen,
    editingUser,
    handleOpenDialog,
    handleCloseDialog,
    handleSubmit,
    handleDelete,
    getStoreNames,
  };
}
